import { parsePhoneNumber } from 'react-phone-number-input';
import { whereEq } from 'ramda';

import { BusinessDocumentSchema } from 'services/businessDocuments';
import { calcProcentege } from 'pages/dashboard/utils';
import { isEmailValid, trimStr } from 'utils/helpers';
import { isValidPhoneNumber } from 'utils/phone';
import {
  BusinessDocumentTypes,
  BusinessDocumentType,
} from 'entities/businessInfo';
import { CountryISOCodes } from 'common/countries';

const mapApplicantData = (applicant) => {
  const applicantPhone = `+${
    applicant.personCountryCallingCode?.replace(/\D/g, '') || ''
  }${applicant.personTelephoneNumber || ''}`;

  return {
    applicantName: `${applicant.givenName1} ${applicant.surnameFirst}`,
    applicantEmail: applicant.emailAddress,
    applicantPhone: applicantPhone === '+' ? '' : applicantPhone,
    applicantRole: applicant.businessesPersonsRole,
    personsId: applicant.personsId,
  };
};

const getNumberByDocumentTypeId = (
  id: number,
  documents: BusinessDocumentSchema[],
) => {
  const document = documents.find(
    (doc) => doc.businessesDocumentsTypesId === id,
  );
  return document?.idnumber || '';
};

const mapBusinessDocuments = (documents: BusinessDocumentSchema[]) => {
  const mappedDocs: BusinessDocumentType[] = documents.map((doc) => ({
    ...doc,
    id: doc.businessesDocumentsId,
  }));

  return mappedDocs;
};

const getUpdateBusinessFields = ({ data, showDoingBusinessAs, business }) => {
  const {
    businessLegalName,
    referenceNumber,
    businessEmailAddress,
    businessesLegalEntityFormTypesId,
    doingBusinessAs,
    dunsnumber,
    primaryURL,
  } = data;

  const emailAddress = isEmailValid(businessEmailAddress)
    ? businessEmailAddress
    : '';

  const fields = {
    businessLegalName,
    emailAddress,
    referenceNumber,
    businessesLegalEntityFormTypesId,
    doingBusinessAs: showDoingBusinessAs ? doingBusinessAs : '',
    dunsnumber,
    primaryURL,
  };

  return whereEq(fields, {
    ...business,
    doingBusinessAs: business.doingBusinessAs || '',
  })
    ? null
    : fields;
};

export const getMappedBusinessInfo = (businessInfo, profile) => {
  const { documents, applicant, erpPreferences } = businessInfo;
  const { business, businessAddress, businessTelephoneNumber } = profile;

  const {
    businessLegalName,
    referenceNumber,
    emailAddress,
    businessesLegalEntityFormTypesId,
    doingBusinessAs,
    dunsnumber,
    primaryURL,
  } = business;
  const { applicantName, applicantEmail, applicantPhone, applicantRole } =
    applicant;
  const {
    city,
    countryIsocode,
    postalCode,
    stateOrProvince,
    addressLine1,
    addressLine2,
  } = businessAddress;

  const businessDocument = documents.find(
    (document) =>
      document.businessesDocumentsTypesId !==
        BusinessDocumentTypes.centralIndexKey &&
      document.businessesDocumentsTypesId !==
        BusinessDocumentTypes.dbaStatement,
  );

  const phone = `+${
    businessTelephoneNumber.countryCallingCode?.replace('+', '') || ''
  }${businessTelephoneNumber.telephoneNumber || ''}`;

  const contactTelephoneNumber =
    erpPreferences?.countryCallingCode && erpPreferences?.telephoneNumber
      ? `+${erpPreferences.countryCallingCode.replace('+', '')}${
          erpPreferences.telephoneNumber
        }`.trim()
      : '';

  return {
    businessLegalName,
    referenceNumber,
    businessEmailAddress: emailAddress,
    businessesLegalEntityFormTypesId,
    doingBusinessAs: trimStr(doingBusinessAs),
    dunsnumber,
    idNumber: businessDocument?.idnumber || '',
    businessesDocumentsTypesId:
      businessDocument?.businessesDocumentsTypesId || '',
    centralIndexKey: getNumberByDocumentTypeId(
      BusinessDocumentTypes.centralIndexKey,
      documents,
    ),
    dbaFile: documents.find(
      (doc) =>
        doc.businessesDocumentsTypesId === BusinessDocumentTypes.dbaStatement,
    ),
    city,
    addressLine1,
    addressLine2,
    countryIsocode,
    postalCode,
    stateOrProvince,
    phone: phone === '+' ? '' : phone,
    applicantName,
    applicantEmail,
    applicantPhone,
    applicantRole,
    primaryURL,
    contactEmailAddress: erpPreferences?.contactEmailAddress
      ? erpPreferences.contactEmailAddress.trim()
      : '',
    contactTelephoneNumber:
      contactTelephoneNumber === '+' ? '' : contactTelephoneNumber,
  };
};

export const getUpdateBusinessERPPreferences = ({ data, erpPreferences }) => {
  if (!isValidPhoneNumber(data.contactTelephoneNumber, data.alpha2Code)) {
    return null;
  }

  const parsePhone = parsePhoneNumber(data.contactTelephoneNumber);

  const contactEmailAddress = isEmailValid(data.contactEmailAddress)
    ? data.contactEmailAddress
    : '';

  const fields: any = {
    contactEmailAddress,
    countryCallingCode: parsePhone ? `+${parsePhone.countryCallingCode}` : '',
    telephoneNumber: parsePhone?.nationalNumber || '',
  };

  const erpFields: any = {
    contactEmailAddress: erpPreferences?.contactEmailAddress || '',
    countryCallingCode: erpPreferences?.countryCallingCode || '',
    telephoneNumber: erpPreferences?.telephoneNumber || '',
  };

  return erpPreferences && whereEq(fields, erpFields) ? null : fields;
};

const getUpdateBusinessPhoneFields = ({ data, businessTelephoneNumber }) => {
  if (!isValidPhoneNumber(data.phone, data.alpha2Code)) {
    return null;
  }

  const parsePhone = parsePhoneNumber(data.phone);

  if (parsePhone) {
    const fields = {
      countryCallingCode: `+${parsePhone.countryCallingCode}`,
      telephoneNumber: `${parsePhone.nationalNumber || ''}`,
    };

    return whereEq(fields, businessTelephoneNumber)
      ? null
      : { ...fields, telephoneNumber1: fields.telephoneNumber };
  }

  return null;
};

export const getAddDocumentFields = ({
  person,
  business,
  file,
  businessesDocumentsTypesId,
}) => {
  const formData = new FormData();
  const fileName = file.name.split('.');

  formData.append('file', file);
  formData.append('isActive', 'true');
  formData.append('createdBy', person.emailAddress);
  formData.append('businessesId', business.businessesId.toString());
  formData.append('blobFileName', file.name);
  formData.append('blobFileExtension', fileName[1]);
  formData.append('blobFileSize', file.size.toString());
  formData.append(
    'businessesDocumentsTypesId',
    businessesDocumentsTypesId.toString(),
  );

  return formData;
};

const checkBusinessInfoSuccess = (data) => {
  const {
    businessAddress,
    documents,
    business,
    businessTelephoneNumber,
    showDoingBusinessAs,
  } = data;

  const { city, postalCode, stateOrProvince, addressLine1 } = businessAddress;
  const document = documents.find(
    (doc) =>
      doc.businessesDocumentsTypesId === BusinessDocumentTypes.dbaStatement,
  );

  if (
    !business.businessLegalName ||
    !business.emailAddress ||
    !addressLine1 ||
    !city ||
    !stateOrProvince ||
    !postalCode ||
    !businessTelephoneNumber.telephoneNumber ||
    (showDoingBusinessAs && (!business.doingBusinessAs || !document))
  ) {
    return false;
  }

  return true;
};

export const calcBusinessInfoPercentage = (data) => {
  const {
    addressLine2,
    centralIndexKey,
    dunsnumber,
    applicantEmail,
    applicantName,
    applicantPhone,
    applicantRole,
    doingBusinessAs,
    dbaFile,
    referenceNumber,
    stateOrProvince,
    ...requiredFields
  } = data;

  const fields = requiredFields;

  if (doingBusinessAs) {
    fields.dbaFile = dbaFile;
  }

  if (data.countryIsocode !== CountryISOCodes.UnitedKingdom) {
    fields.stateOrProvince = stateOrProvince;
  }

  return calcProcentege(fields);
};

const getUpdateBusinessAddressFields = ({ data, businessAddress }) => {
  const {
    addressLine1,
    addressLine2,
    city,
    countryIsocode,
    postalCode,
    stateOrProvince,
  } = data;

  const fields = {
    addressLine1,
    addressLine2: addressLine2 === '' ? ' ' : addressLine2,
    city,
    countryIsocode,
    postalCode,
    stateOrProvince: stateOrProvince || null,
  };

  return whereEq(fields, businessAddress) ? null : fields;
};

export {
  mapApplicantData,
  mapBusinessDocuments,
  getNumberByDocumentTypeId,
  getUpdateBusinessFields,
  getUpdateBusinessAddressFields,
  getUpdateBusinessPhoneFields,
  checkBusinessInfoSuccess,
};

// --- ID number (CRN / UTR / Charity number) formatting & validation --------
// Shared by the IdNumberInput component and the save thunk so the same rules
// are enforced on input and again before persisting to the backend.

export const idNumberMasks: Record<string, string> = {
  UTR: '9999999999', // Unique Tax Payer Reference Number
};

export const idNumberMaxLength: Record<string, number> = {
  CRN: 8,
  CHARITY_NUMBER: 15,
};

export const idNumberMinLength: Record<string, number> = {
  CHARITY_NUMBER: 6,
};

// Types that must have an exact number of characters.
export const idNumberExactLength: Record<string, number> = {
  CRN: 8,
  UTR: 10,
};

export const idNumberPatterns: Record<string, RegExp> = {
  CRN: /^([A-Za-z]{0,2}$|[A-Za-z]{2}\d{0,6}$|\d{0,8}$)/,
  CHARITY_NUMBER: /^[A-Za-z0-9]*$/,
  UTR: /^\d*$/,
};

export const idNumberPlaceholders: Record<string, string> = {
  UTR: '0000000000',
  CRN: 'AA000000',
  CHARITY_NUMBER: 'AB12345',
};

// Types whose letters should be normalized to uppercase.
export const idNumberUppercaseTypes = ['CRN', 'CHARITY_NUMBER'];

export const idNumberErrorKeys: Record<string, string> = {
  charityMinLength: 'dashboard:fields.idNumber.validation.charityMinLength',
  CRN: 'dashboard:fields.idNumber.validation.crnLength',
  UTR: 'dashboard:fields.idNumber.validation.utrLength',
};

export const normalizeIdNumber = (typeCode: string, value: string): string =>
  idNumberUppercaseTypes.includes(typeCode) ? value.toUpperCase() : value;

// Returns the i18n key describing the problem, or null when valid.
// An empty value is treated as valid here — the "required" check is separate.
export const validateIdNumber = (
  typeCode: string,
  value: string | undefined,
): string | null => {
  const trimmed = (value ?? '').trim();

  if (!trimmed) return null;

  const minLength = idNumberMinLength[typeCode];

  if (minLength && trimmed.length < minLength) {
    return idNumberErrorKeys.charityMinLength;
  }

  const exactLength = idNumberExactLength[typeCode];

  if (exactLength && trimmed.length !== exactLength) {
    return idNumberErrorKeys[typeCode] ?? null;
  }

  const pattern = idNumberPatterns[typeCode];

  if (pattern && !pattern.test(trimmed)) {
    return idNumberErrorKeys[typeCode] ?? null;
  }

  return null;
};
